import { useState, useEffect } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Navbar from "../../../constants/Navbar";
import { useAlert } from '../../../constants/AlertProvider';
import { useNavigate } from "react-router";
import { ApiService } from "../../../api/services.gen";
import { getUserDetails } from "../../../utils/getUserDetails";
import axios from "axios";

export default function OnboardingSkills() {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [applicationId, setApplicationId] = useState(null);
    const navigate = useNavigate();
    const showAlert = useAlert();

    useEffect(() => {
        const fetchProfile = async () => {
            const userDetails = await getUserDetails();
            const userId = userDetails?.id;
            if (!userId) {
                console.error("User ID not found");
                return;
            }
            try {
                const userRes = await axios.get(`/api/users/${userId}/`, { withCredentials: true })
                const appId = userRes.data.applicationids[0].id;
                setApplicationId(appId);
            } catch {
                showAlert("Unable to fetch app ID", "error");
            }
        };
        fetchProfile();
    }, []);

    useEffect(() => {
        const fetchSkills = async () => {
            if (query.trim().length < 2) {
                setSuggestions([]);
                return;
            }
            try {
                const res = await axios.get(`/api/matching-skills/autocomplete/?q=${query}`);
                setSuggestions(res.data.skills);
            } catch (err) {
                console.error("Error fetching skills:", err);
            }
        };
        const delayDebounce = setTimeout(fetchSkills, 300);
        return () => clearTimeout(delayDebounce);
    }, [query]);

    const addSkill = (skillObj) => {
        if (!selectedSkills.some(s => s.id === skillObj.id)) {
            setSelectedSkills([...selectedSkills, skillObj]);
        }
        setQuery("");
        setSuggestions([]);
    };

    const removeSkill = (skillObj) => {
        setSelectedSkills(selectedSkills.filter((s) => s.id !== skillObj.id));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && query.trim() !== "") {
            e.preventDefault();
            addSkill({ name: query.trim(), id: null });
        }
    };

    const handleNext = async () => {
        if (!applicationId) {
            showAlert("Application ID not found", "error");
            return;
        }
        try {
            for (const skill of selectedSkills) {
                await ApiService.apiSkillCreate({
                    requestBody: {
                        application: applicationId,
                        name: skill.name,
                        score: 5,
                        matching_skill_id: skill.id || null
                    }
                });
            }
            navigate("/setprofile");
        } catch (err) {
            showAlert("Failed to save skills", "error");
            console.error("Failed to save skills:", err);
        }
    };

    return (
        <>
            <Navbar />
            <div className="p-4 flex flex-col items-center">
                <div className="flex flex-col items-center justify-center p-4 md:p-6 w-full md:w-3/5 gap-y-6">
                    <div className="w-full flex flex-col items-center justify-center p-6 gap-y-4">
                        <h1 className="text-gray-900 dark:text-gray-100 font-bold text-xl md:text-3xl">What are you great at?</h1>
                        <p className="text-gray-900 dark:text-gray-100 font-normal text-[10px] md:text-[14px]">Add your core expertise and skills. This is how we match you to the right projects. Be specific and you can add more later!</p>
                    </div>
                    <div className="w-full flex flex-col items-center gap-y-4">
                        <div className="w-full md:w-4/5 flex flex-col gap-y-2 mb-4">
                            <label htmlFor="skill" className="text-gray-900 dark:text-gray-100 font-semibold text-[16px]">
                                Skills and Expertise
                            </label>
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 dark:text-gray-400" />
                                <input
                                    type="text"
                                    id="skill"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full pl-10 pr-4 h-12 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:border-blue-500"
                                    placeholder="Search..."
                                    required
                                />
                                {suggestions.length > 0 && (
                                    <ul className="absolute top-full mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto z-10">
                                        {suggestions.map((skill, idx) => (
                                            <li
                                                key={skill.id || idx}
                                                onClick={() => addSkill(skill)}
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-[14px] text-gray-900 dark:text-gray-100"
                                            >
                                                {skill.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div className="w-4/5 flex flex-wrap gap-2">
                            {selectedSkills.map((skill, idx) => (
                                <div
                                    key={skill.id || idx}
                                    className="p-2 h-10 border-2 border-blue-700 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30 flex flex-row gap-x-2 items-center rounded-lg"
                                >
                                    <span className="text-blue-700 dark:text-blue-300 text-[16px] font-medium">{skill.name}</span>
                                    <button type="button" onClick={() => removeSkill(skill)}>
                                        <XMarkIcon className="size-4 text-blue-700/50 cursor-pointer" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="fixed bottom-6 right-8 z-50">
                <button type="button" onClick={handleNext} className="text-white h-10 font-medium text-sm bg-blue-700 px-4 rounded-lg cursor-pointer hover:bg-blue-600">
                    Next, review your profile
                </button>
            </div>
        </>
    );
}